import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {debounceTime, distinctUntilChanged, map} from 'rxjs/operators';
import {MapConfig} from '@ax/ax-angular-map-common';
import {
  IFederationDomain,
  IFederationPolicy,
  TransportFederationDomain,
  TransportFederationPolicy
} from '../model/FederationPolicy';
import * as _ from 'lodash';
import {UUID} from '@ax/ax-common';
import {Point, Polygon} from "@ax/ax-angular-map-common/lib/models/GeoJSON";
import {IXYZLayerConfig} from "@ax/ax-angular-map-common/lib/models/XYZLayerConfig";
import {IMapBoxLayerConfig} from "@ax/ax-angular-map-common/lib/models/MapBoxLayerConfig";
import {IBingLayerConfig} from "@ax/ax-angular-map-common/lib/models/BingLayerConfig";
import {ICesiumIonTileConfig} from "@ax/ax-angular-map-common/lib/models/CesiumIonLayerConfig";
import {ICesiumTerrainLayerConfig} from "@ax/ax-angular-map-common/lib/models/CesiumTerrainLayerConfig";
import {IEllipsoidTerrainLayerConfig} from "@ax/ax-angular-map-common/lib/models/EllipsoidTerrainLayerConfig";
import {IWMSLayerConfig} from "@ax/ax-angular-map-common/lib/models/WMSLayerConfig";
import {IBaseLayerConfig} from "@ax/ax-angular-map-common/lib/models/api";

export interface IUssSettings {
  title: string;
  classificationLevel: string;
  mapConfig: TransportMapConfig;
  knownFederationDomains: TransportFederationDomain[];
  defaultFederationPolicy: TransportFederationPolicy;
  experimentalSettings: TransportExperimentalSettings;
  laanc?: LaancSettings;
}

export interface TransportExperimentalSettings {
  enableDashboardSupport: boolean;
  enableFederationSupport: boolean;
  enableDirectMessagingSupport: boolean;
}

export interface TransportMapConfig {
  layerConfigs: TransportLayerConfig[];
  defaultImageryLayerId: string | null;
  defaultTerrainLayerId: string | null;
  centerPoint: Point | null;
  defaultBounds: Polygon | null;
}

export declare type TransportLayerConfig = IXYZLayerConfig | IMapBoxLayerConfig | IBingLayerConfig | ICesiumIonTileConfig |
  ICesiumTerrainLayerConfig | IEllipsoidTerrainLayerConfig | TransportAzureMapTileConfig | IWMSLayerConfig;

export interface TransportAzureMapTileConfig extends IBaseLayerConfig {
  accessToken: string;
  layerIds: string[];
}

export interface ExperimentalSettings {
  enableDashboardSupport: boolean;
  enableFederationSupport: boolean;
  enableDirectMessagingSupport: boolean;
}

export interface LaancSettings {
  enabled?: boolean;
  faaMessageEnabled: boolean;
  faaMessage: string;
}

export interface UssSettings {
  title: string;
  classificationLevel: string;
  mapConfig: MapConfig;
  knownFederationDomains: IFederationDomain[];
  defaultFederationPolicy: IFederationPolicy;
  experimentalSettings: ExperimentalSettings;
  laanc?: LaancSettings;
}

export const DEFAULT_FEDERATION_POLICY: IFederationPolicy = {
  allowAll: true,
  domains: [
    'test',
    'comm'
  ]
};

export const DEFAULT_EXPERIMENTAL_SETTINGS: ExperimentalSettings = {
  enableDashboardSupport: false,
  enableFederationSupport: false,
  enableDirectMessagingSupport: false
};

export const DEFAULT_LAANC_SETTINGS = {
  enabled: false,
  faaMessageEnabled: false,
  faaMessage: ''
}

const currentId = UUID.generateUUID();

@Injectable({
  providedIn: 'root'
})
export abstract class SettingsService {
  private readonly currentId: string;

  protected constructor() {
    // Empty constructor
  }

  getCurrentId(): string {
    return currentId;
  }

  getTitle(): Observable<string> {
    return this.getUssName();
  }

  getClassificationLevel(): Observable<string> {
    return this.getDistinctRawSettings().pipe(map(s => s.classificationLevel || 'Unclassified'));
  }

  getUssName(): Observable<string> {
    return this.getDistinctRawSettings().pipe(map(s => s.title || 'AX Federal USS'));
  }

  getMapConfig(): Observable<MapConfig> {
    return this.getDistinctRawSettings().pipe(map(s => s.mapConfig));
  }

  abstract getLogoUrlForOrg(org: string): Observable<string>;

  abstract getRawSettings(): Observable<UssSettings>;

  getDistinctRawSettings(): Observable<UssSettings> {
    return this.getRawSettings().pipe(
      distinctUntilChanged((prev, curr) => {
        return _.isEqual(prev, curr);
      }),
      debounceTime(100)
    );
  }

  abstract refreshSettings(): void;

  abstract updateSettings(settings: UssSettings): Observable<boolean>;

  abstract createSettings(settings: UssSettings): Observable<boolean>;

  getKnownFederationDomains(): Observable<IFederationDomain[]> {
    return this.getRawSettings()
      .pipe(map(s => s.knownFederationDomains || []))
      .pipe(map(_.cloneDeep));
  }

  getDefaultFederationPolicy(): Observable<IFederationPolicy> {
    return this.getRawSettings()
      .pipe(map(s => s.defaultFederationPolicy || DEFAULT_FEDERATION_POLICY))
      .pipe(map(_.cloneDeep));

  }

  getExperimentalSettings(): Observable<ExperimentalSettings> {
    return this.getRawSettings()
      .pipe(map(s => s.experimentalSettings || DEFAULT_EXPERIMENTAL_SETTINGS))
      .pipe(map(_.cloneDeep));
  }

  getLaancSettings(): Observable<LaancSettings> {
    return this.getRawSettings()
      .pipe(map(s => s.laanc || DEFAULT_LAANC_SETTINGS))
      .pipe(map(_.cloneDeep));
  }
}
